/**
 * Popular Topics with AI-Optimized Prompts
 * 
 * Each topic includes:
 * - id: Unique identifier (also used for icon mapping)
 * - label: Display name for the UI
 * - description: Brief description of what the topic covers
 * - optimizedPrompt: AI-optimized prompt that gets sent to the LLM
 *   - Includes context, specific instructions, and desired output format
 *   - Designed to elicit detailed, actionable responses
 * - labelEs: Spanish label
 * - descriptionEs: Spanish description
 * - optimizedPromptEs: Spanish optimized prompt
 */

export interface PopularTopic {
  id: string;
  label: string;
  description: string;
  optimizedPrompt: string;
  labelEs: string;
  descriptionEs: string;
  optimizedPromptEs: string;
}

export const POPULAR_TOPICS: PopularTopic[] = [
  {
    id: 'sales-strategy',
    label: 'Sales Strategy',
    description: 'Get insights on customer deals and sales opportunities',
    optimizedPrompt: `I need help with sales strategy and customer management. Please provide guidance on:

1. **Customer Analysis** - How can I better understand our customer base and identify upsell opportunities?
2. **Deal Strategy** - What are best practices for managing and closing deals?
3. **Pipeline Management** - How should I organize and prioritize our sales pipeline?
4. **Sales Process** - What's an effective sales process for MSP services?
5. **Customer Retention** - How can we improve customer retention and reduce churn?
6. **Pricing Strategy** - What are best practices for MSP service pricing?
7. **Next Steps** - What specific actions should I take this week to improve sales?

Please provide actionable, specific guidance with real-world MSP examples.`,
    labelEs: 'Estrategia de Ventas',
    descriptionEs: 'Obtén información sobre ofertas de clientes y oportunidades de ventas',
    optimizedPromptEs: `Necesito ayuda con estrategia de ventas y gestión de clientes. Por favor, proporciona orientación sobre:

1. **Análisis de Clientes** - ¿Cómo puedo entender mejor nuestra base de clientes e identificar oportunidades de venta cruzada?
2. **Estrategia de Ofertas** - ¿Cuáles son las mejores prácticas para gestionar y cerrar ofertas?
3. **Gestión de Pipeline** - ¿Cómo debo organizar y priorizar nuestro pipeline de ventas?
4. **Proceso de Ventas** - ¿Cuál es un proceso de ventas efectivo para servicios MSP?
5. **Retención de Clientes** - ¿Cómo podemos mejorar la retención de clientes y reducir la rotación?
6. **Estrategia de Precios** - ¿Cuáles son las mejores prácticas para precios de servicios MSP?
7. **Próximos Pasos** - ¿Qué acciones específicas debo tomar esta semana para mejorar las ventas?

Por favor, proporciona orientación práctica y específica con ejemplos reales de MSP.`,
  },
  {
    id: 'ticket-management',
    label: 'Ticket Management',
    description: 'Best practices for handling support tickets and customer issues',
    optimizedPrompt: `I need help with ticket management and customer support. Please provide guidance on:

1. **Ticket Prioritization** - How should I prioritize support tickets based on urgency and impact?
2. **Resolution Process** - What's an effective process for resolving customer issues quickly?
3. **Escalation** - When and how should tickets be escalated to senior staff?
4. **Response Times** - What are realistic SLA targets for different ticket types?
5. **Customer Communication** - How should I communicate with customers during ticket resolution?
6. **Documentation** - What should be documented for each ticket for future reference?
7. **Patterns** - How can I identify patterns in tickets to prevent future issues?
8. **Team Coordination** - How should tickets be assigned and tracked across the team?

Please provide actionable guidance with MSP support best practices.`,
    labelEs: 'Gestión de Tickets',
    descriptionEs: 'Mejores prácticas para manejar tickets de soporte y problemas de clientes',
    optimizedPromptEs: `Necesito ayuda con gestión de tickets y soporte al cliente. Por favor, proporciona orientación sobre:

1. **Priorización de Tickets** - ¿Cómo debo priorizar los tickets de soporte según urgencia e impacto?
2. **Proceso de Resolución** - ¿Cuál es un proceso efectivo para resolver problemas de clientes rápidamente?
3. **Escalada** - ¿Cuándo y cómo deben escalarse los tickets al personal senior?
4. **Tiempos de Respuesta** - ¿Cuáles son objetivos realistas de SLA para diferentes tipos de tickets?
5. **Comunicación con Clientes** - ¿Cómo debo comunicarme con los clientes durante la resolución de tickets?
6. **Documentación** - ¿Qué debe documentarse para cada ticket para referencia futura?
7. **Patrones** - ¿Cómo puedo identificar patrones en tickets para prevenir problemas futuros?
8. **Coordinación de Equipo** - ¿Cómo deben asignarse y rastrearse los tickets en el equipo?

Por favor, proporciona orientación práctica con mejores prácticas de soporte MSP.`,
  },
  {
    id: 'operations-optimization',
    label: 'Operations Optimization',
    description: 'Improve workflows and operational efficiency',
    optimizedPrompt: `I need help optimizing our operations and workflows. Please provide guidance on:

1. **Process Improvement** - What are the biggest opportunities to improve our operational efficiency?
2. **Workflow Optimization** - How can we streamline our current workflows?
3. **Resource Allocation** - How should we allocate team resources for maximum efficiency?
4. **Time Management** - What are best practices for managing team time and workload?
5. **Automation** - What tasks could be automated to save time and reduce errors?
6. **Documentation** - How should we document processes for consistency and training?
7. **Metrics** - What KPIs should we track to measure operational performance?
8. **Team Coordination** - How can we improve communication and coordination across the team?

Please provide specific, actionable recommendations for MSP operations.`,
    labelEs: 'Optimización de Operaciones',
    descriptionEs: 'Mejora flujos de trabajo y eficiencia operacional',
    optimizedPromptEs: `Necesito ayuda optimizando nuestras operaciones y flujos de trabajo. Por favor, proporciona orientación sobre:

1. **Mejora de Procesos** - ¿Cuáles son las mayores oportunidades para mejorar nuestra eficiencia operacional?
2. **Optimización de Flujos** - ¿Cómo podemos simplificar nuestros flujos de trabajo actuales?
3. **Asignación de Recursos** - ¿Cómo debemos asignar recursos del equipo para máxima eficiencia?
4. **Gestión del Tiempo** - ¿Cuáles son las mejores prácticas para gestionar el tiempo y carga de trabajo del equipo?
5. **Automatización** - ¿Qué tareas podrían automatizarse para ahorrar tiempo y reducir errores?
6. **Documentación** - ¿Cómo debemos documentar procesos para consistencia y capacitación?
7. **Métricas** - ¿Qué KPIs debemos rastrear para medir el desempeño operacional?
8. **Coordinación de Equipo** - ¿Cómo podemos mejorar la comunicación y coordinación en el equipo?

Por favor, proporciona recomendaciones específicas y prácticas para operaciones MSP.`,
  },
  {
    id: 'team-productivity',
    label: 'Team Productivity',
    description: 'Boost team performance and collaboration',
    optimizedPrompt: `I need help improving team productivity and collaboration. Please provide guidance on:

1. **Team Performance** - What are the key factors that drive team productivity?
2. **Collaboration** - How can we improve collaboration and communication within the team?
3. **Motivation** - What strategies help keep the team motivated and engaged?
4. **Training** - What training and development opportunities would benefit the team?
5. **Tools & Systems** - What tools and systems can help the team work more efficiently?
6. **Accountability** - How should we set goals and track progress?
7. **Feedback** - How can we provide effective feedback to improve performance?
8. **Work-Life Balance** - How can we maintain healthy work-life balance while staying productive?

Please provide practical recommendations for MSP team management.`,
    labelEs: 'Productividad del Equipo',
    descriptionEs: 'Aumenta el desempeño y la colaboración del equipo',
    optimizedPromptEs: `Necesito ayuda mejorando la productividad y colaboración del equipo. Por favor, proporciona orientación sobre:

1. **Desempeño del Equipo** - ¿Cuáles son los factores clave que impulsan la productividad del equipo?
2. **Colaboración** - ¿Cómo podemos mejorar la colaboración y comunicación dentro del equipo?
3. **Motivación** - ¿Qué estrategias ayudan a mantener el equipo motivado e involucrado?
4. **Capacitación** - ¿Qué oportunidades de capacitación y desarrollo beneficiarían al equipo?
5. **Herramientas y Sistemas** - ¿Qué herramientas y sistemas pueden ayudar al equipo a trabajar más eficientemente?
6. **Responsabilidad** - ¿Cómo debemos establecer objetivos y rastrear el progreso?
7. **Retroalimentación** - ¿Cómo podemos proporcionar retroalimentación efectiva para mejorar el desempeño?
8. **Equilibrio Trabajo-Vida** - ¿Cómo podemos mantener un equilibrio saludable trabajo-vida mientras permanecemos productivos?

Por favor, proporciona recomendaciones prácticas para la gestión de equipos MSP.`,
  },
];

/**
 * Get a popular topic by ID
 */
export function getPopularTopicById(id: string): PopularTopic | undefined {
  return POPULAR_TOPICS.find((topic) => topic.id === id);
}

/**
 * Get all popular topics for display
 */
export function getAllPopularTopics(): PopularTopic[] {
  return POPULAR_TOPICS;
}

/**
 * Get popular topics for a specific locale
 */
export function getPopularTopicsForLocale(locale: string = 'en'): PopularTopic[] {
  if (locale === 'es') {
    return POPULAR_TOPICS.map((topic) => ({
      ...topic,
      label: topic.labelEs,
      description: topic.descriptionEs,
      optimizedPrompt: topic.optimizedPromptEs,
    }));
  }
  return POPULAR_TOPICS;
}

/**
 * Get the optimized prompt for a topic
 * Returns the optimized prompt if found, otherwise returns the label as fallback
 */
export function getOptimizedPrompt(topicId: string, locale: string = 'en'): string {
  const topic = getPopularTopicById(topicId);
  if (!topic) return '';
  
  if (locale === 'es') {
    return topic.optimizedPromptEs;
  }
  return topic.optimizedPrompt;
}

/**
 * Get topic by label (for backward compatibility with UI)
 */
export function getTopicByLabel(label: string, locale: string = 'en'): PopularTopic | undefined {
  if (locale === 'es') {
    return POPULAR_TOPICS.find((topic) => topic.labelEs === label);
  }
  return POPULAR_TOPICS.find((topic) => topic.label === label);
}

/**
 * Get optimized prompt by label
 */
export function getOptimizedPromptByLabel(label: string, locale: string = 'en'): string {
  const topic = getTopicByLabel(label, locale);
  if (!topic) return label;
  
  if (locale === 'es') {
    return topic.optimizedPromptEs;
  }
  return topic.optimizedPrompt;
}

